/** Operations (in-stay service-request / task) queries. */

import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { queryKeys } from '../query-keys';
import { useApiClient } from '../react/ApiClientProvider';
import {
  type RoomStatus,
  roomStatusSchema,
  type ServiceRequest,
  serviceRequestSchema,
} from '../responses';
import { unwrap } from './_helpers';

export type ServiceRequestsFilters = {
  limit?: number;
};

/** `GET /operations/service-requests` — open/active tasks for the hotel. */
export function useServiceRequests(
  tenantId: string,
  hotelId: string,
  filters?: ServiceRequestsFilters,
): UseQueryResult<ServiceRequest[]> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.tasks(tenantId, hotelId, filters),
    queryFn: async () => {
      const result = await client.GET(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/operations/service-requests',
        {
          params: { path: { tenantId, hotelId }, query: { limit: filters?.limit } },
          parseAs: 'json',
        },
      );
      return unwrap(result, z.array(serviceRequestSchema));
    },
  });
}

/** `GET /operations/service-requests/exceptions` — SLA-breached / escalated tasks. */
export function useServiceRequestExceptions(
  tenantId: string,
  hotelId: string,
): UseQueryResult<ServiceRequest[]> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.taskExceptions(tenantId, hotelId),
    queryFn: async () => {
      const result = await client.GET(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/operations/service-requests/exceptions',
        { params: { path: { tenantId, hotelId } }, parseAs: 'json' },
      );
      return unwrap(result, z.array(serviceRequestSchema));
    },
  });
}

/** `GET /operations/service-requests/:requestId`. */
export function useServiceRequest(
  tenantId: string,
  hotelId: string,
  requestId: string,
): UseQueryResult<ServiceRequest> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.task(tenantId, hotelId, requestId),
    queryFn: async () => {
      const result = await client.GET(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/operations/service-requests/{requestId}',
        { params: { path: { tenantId, hotelId, requestId } }, parseAs: 'json' },
      );
      return unwrap(result, serviceRequestSchema);
    },
    enabled: requestId.length > 0,
  });
}

/** `GET /operations/rooms/:roomId/status` — current status + active requests for a room. */
export function useRoomStatus(
  tenantId: string,
  hotelId: string,
  roomId: string,
): UseQueryResult<RoomStatus> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.roomStatus(tenantId, hotelId, roomId),
    queryFn: async () => {
      const result = await client.GET(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/operations/rooms/{roomId}/status',
        { params: { path: { tenantId, hotelId, roomId } }, parseAs: 'json' },
      );
      return unwrap(result, roomStatusSchema);
    },
    enabled: roomId.length > 0,
  });
}
